import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';

declare const __SUPABASE_URL__: string | undefined;
declare const __SUPABASE_ANON_KEY__: string | undefined;

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string;
}

const SUPABASE_URL = (__SUPABASE_URL__ || '').trim().replace(/\/$/, '');
const SUPABASE_ANON_KEY = (__SUPABASE_ANON_KEY__ || '').trim();

let client: SupabaseClient | null = null;

export function isSupabaseAuthConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

export function getSupabaseAuthConfigStatus(): { configured: boolean; missing: string[] } {
  const missing: string[] = [];
  if (!SUPABASE_URL) {
    missing.push('VITE_SUPABASE_URL');
  }
  if (!SUPABASE_ANON_KEY) {
    missing.push('VITE_SUPABASE_ANON_KEY');
  }
  return {
    configured: missing.length === 0,
    missing
  };
}

export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  if (!isSupabaseAuthConfigured()) {
    return null;
  }

  const session = await getCurrentAuthSession();
  return toAuthUser(session?.user || null);
}

export async function getAccessToken(): Promise<string | null> {
  if (!isSupabaseAuthConfigured()) {
    return null;
  }

  const session = await getCurrentAuthSession();
  return session?.access_token || null;
}

export function onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
  if (!isSupabaseAuthConfigured()) {
    return () => undefined;
  }

  const { data } = getClient().auth.onAuthStateChange((_event, session) => {
    callback(toAuthUser(session?.user || null));
  });

  return () => {
    data.subscription.unsubscribe();
  };
}

export async function signInWithGoogle(): Promise<void> {
  const w = 480;
  const h = 640;
  const left = Math.round(screen.width / 2 - w / 2);
  const top = Math.round(screen.height / 2 - h / 2);
  const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`;
  let popup: Window | null = null;

  // Safari is strict about popups: open the window synchronously from the
  // click handler, then navigate it after Supabase returns the OAuth URL.
  try { localStorage.setItem('mp-auth-popup', '1'); } catch (_) {}
  try {
    popup = window.open('', 'motionpitch-auth', features);
  } catch (_) {
    popup = null;
  }

  const { data, error } = await getClient().auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getAuthRedirectUrl(),
      skipBrowserRedirect: true
    }
  });

  if (error || !data.url) {
    try { popup?.close(); } catch (_) {}
    try { localStorage.removeItem('mp-auth-popup'); } catch (_) {}
    throw error || new Error('Failed to get Google sign-in URL');
  }

  if (popup) {
    popup.location.href = data.url;
    try { popup.focus(); } catch (_) {}
    return;
  }

  // Popup blocked — fall back to redirect.
  try { localStorage.removeItem('mp-auth-popup'); } catch (_) {}
  // Save current hash so we can restore it after the OAuth redirect (mobile flow)
  try {
    const hash = window.location.hash;
    if (hash && hash !== '#') {
      localStorage.setItem('mp-auth-return-hash', hash);
    }
  } catch (_) {}
  window.location.href = data.url;
  // Auth state updates in this window automatically via shared localStorage
}

export async function signInWithEmail(email: string): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error('Enter your email.');
  }

  const { error } = await getClient().auth.signInWithOtp({
    email: normalizedEmail,
    options: {
      emailRedirectTo: getAuthRedirectUrl(),
      shouldCreateUser: true
    }
  });

  if (error) {
    throw error;
  }
}

export async function signOut(): Promise<void> {
  if (!isSupabaseAuthConfigured()) {
    return;
  }

  const { error } = await getClient().auth.signOut();
  if (error) {
    throw error;
  }
}

async function getCurrentAuthSession(): Promise<Session | null> {
  const { data, error } = await getClient().auth.getSession();
  if (error) {
    throw error;
  }
  return data.session;
}

function getClient(): SupabaseClient {
  if (!isSupabaseAuthConfigured()) {
    const missing = getSupabaseAuthConfigStatus().missing.join(', ');
    throw new Error(`Supabase auth is not configured: ${missing}`);
  }

  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
  }

  return client;
}

function getAuthRedirectUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) {
    return null;
  }

  const metadata = user.user_metadata || {};
  const email = user.email || '';
  const displayName =
    toStringValue(metadata.full_name) ||
    toStringValue(metadata.name) ||
    email.split('@')[0] ||
    'User';

  return {
    id: user.id,
    email,
    displayName,
    avatarUrl: toStringValue(metadata.avatar_url) || toStringValue(metadata.picture)
  };
}

function toStringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
